///
/// EarthBrowser
///
/// Created by Matt Giger on 1/24/2026
/// Copyright (c) 2026$ Geodata Labs
///

import { TextureFrame } from "@/earthbrowser/datasets/TextureSeries"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { cCacheAgeOneWeek, type FetchPriority } from "@/earthbrowser/utils/loaders/FetchLoader"
import { datasetUrl } from "@/utils/datasetUrl"
import { type time, type timedelta } from "@stovokor/math/time"
import { ComponentType, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * Pressure field texture frame.
 * Packed RG16 in an RGBA8 PNG:
 * - R = low byte
 * - G = high byte
 * - A = validity mask
 */
export class PressureFieldTextureFrame extends TextureFrame {
	/** Optional RG32F displacement field toward the NEXT frame (UV units),
	 *  used by the isoline shader to advect fields while blending */
	public flowTexture?: Texture

	constructor(context: StovokorContext, url: string, startTime: time, duration: timedelta) {
		super(context, url, startTime, duration)
	}

	public override load(priority: FetchPriority = "auto"): void {
		if (this.loading || this.available) {
			return
		}
		this.loading = true
		if (this.texture === this.context.blankTexture) {
			this.createNewTexture()
		}
		this.textureLoader = new ImageLoader(datasetUrl(this.url), { lifetime: cCacheAgeOneWeek, priority })
		this.textureLoader.loadIntoTexture(this.texture)
			.then((success) => {
				this.available = success
				this.metadata = this.textureLoader?.metadata ?? {}
				this.loading = false
			})
			.catch(_e => { this.loading = false })
			.finally(() => { this.textureLoader = undefined })
	}

	protected override createNewTexture(): void {
		this.texture = new Texture(
			this.context,
			1,
			1,
			PixelFormat.RGBA,
			PixelFormat.RGBA,
			ComponentType.UnsignedByte,
			TextureFilter.LinearMipmapNearest,
			TextureFilter.Linear,
			TextureWrap.Repeat,
			TextureWrap.ClampToEdge
		)
	}
}

